/**
 * Date formatting utilities for facturation module
 * Provides consistent, locale-aware date formatting across all facturation views
 */

/**
 * Format a date to locale string (DD/MM/YYYY)
 * @param date - Date string or Date object
 * @param locale - Locale code (default: 'fr-BE')
 * @returns Formatted date string or '-' if invalid
 */
export const formatDate = (date: string | Date | null | undefined, locale = 'fr-BE'): string => {
  if (!date) return '-';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '-';
  }
};

/**
 * Format a date to locale string with short month name (DD MMM YYYY)
 * @param date - Date string or Date object
 * @param locale - Locale code (default: 'fr-BE')
 * @returns Formatted date string or '-' if invalid
 */
export const formatDateShort = (date: string | Date | null | undefined, locale = 'fr-BE'): string => {
  if (!date) return '-';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '-';
  }
};

/**
 * Format a date with time to locale string (DD/MM/YYYY HH:MM)
 * @param date - Date string or Date object
 * @param locale - Locale code (default: 'fr-BE')
 * @returns Formatted datetime string or '-' if invalid
 */
export const formatDateTime = (date: string | Date | null | undefined, locale = 'fr-BE'): string => {
  if (!date) return '-';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '-';
  }
};

/**
 * Convert a date to ISO date string (YYYY-MM-DD) for input fields
 * @param date - Date object or string
 * @returns ISO date string or empty string if invalid
 */
export const toISODateString = (date: Date | string | null | undefined): string => {
  if (!date) return '';
  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

/**
 * Add days to a date and return ISO date string
 * @param date - Date object
 * @param days - Number of days to add
 * @returns ISO date string (YYYY-MM-DD)
 */
export const addDays = (date: Date, days: number): string => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result.toISOString().split('T')[0];
};
